import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, useLocation } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { darkTheme, lightTheme } from './theme/theme';
import Layout from './components/layout/Layout';
import { AuthProvider } from './context/AuthContext';
import { ChatProvider } from './context/ChatContext';
import { ThemeContext } from './context/ThemeContext';
import { SubscriptionProvider } from './contexts/SubscriptionContext';
import { useSubscription } from './contexts/SubscriptionContext';

// Component to force subscription context refresh on navigation
const RouteChangeHandler: React.FC = () => {
  const location = useLocation();
  const { pathname } = location;
  const { refreshSubscription } = useSubscription();

  // Add useEffect for refreshing subscription after payment
  useEffect(() => {
    console.log('App navigation to:', pathname);
    
    // If navigating from payment success or cancel page, refresh subscription status
    if (pathname === '/payment-success' || pathname === '/payment-cancel' || pathname.includes('/checkout')) {
      console.log('Detected payment page navigation, refreshing subscription status');
      refreshSubscription()
        .then(() => console.log('Subscription refreshed after payment navigation'))
        .catch(err => console.error('Error refreshing subscription after payment:', err));
    }
  }, [pathname, refreshSubscription]);

  return null;
};

// Wrapper to access context hooks - needed because main App component renders providers
const AppWithProviders = () => {
  return (
    <Router>
      <AppRoutes />
    </Router>
  );
};

// Inner component with routes that can access all providers
const AppRoutes = () => {
  return (
    <>
      <RouteChangeHandler />
      <Layout />
    </>
  );
};

function App() {
  const [isDarkMode, setIsDarkMode] = useState(true);
  const theme = isDarkMode ? darkTheme : lightTheme;

  const toggleTheme = () => {
    setIsDarkMode(!isDarkMode);
  };

  return (
    <ThemeContext.Provider value={{ isDarkMode, toggleTheme }}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <AuthProvider>
          <SubscriptionProvider>
            <ChatProvider>
              <AppWithProviders />
            </ChatProvider>
          </SubscriptionProvider>
        </AuthProvider>
      </ThemeProvider>
    </ThemeContext.Provider>
  );
}

export default App; 